import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Mail, Loader2, Shield } from "lucide-react";

export default function AuthGate({ onClose }) {
  const { signInWithEmail, startGoogleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await signInWithEmail(email.trim(), name.trim());
      toast.success("Signed in");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Sign-in failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="auth-gate-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/85 p-4 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-md border border-slate-200 bg-white">
        {onClose && (
          <button
            onClick={onClose}
            data-testid="auth-gate-close"
            className="absolute right-3 top-3 border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300 hover:text-slate-900"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="border-b border-slate-200 p-6">
          <div className="mb-2 flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#2563EB]" />
            <span className="overline text-[#2563EB]">FREE ACCESS</span>
          </div>
          <h2 className="font-serif text-2xl font-semibold leading-tight text-slate-900">
            Continue to Qualmitta
          </h2>
          <p className="mt-1.5 text-sm text-slate-500">
            Free, unlimited access. We just need an email to save your work.
          </p>
        </div>

        <div className="space-y-4 p-6">
          <button
            data-testid="auth-google-btn"
            onClick={startGoogleLogin}
            disabled={busy}
            className="group inline-flex w-full items-center justify-center gap-3 border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-black transition-all hover:border-[#2563EB] hover:bg-[#2563EB] hover:text-white disabled:opacity-50"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-100" />
            <span className="overline text-slate-400">OR</span>
            <div className="h-px flex-1 bg-slate-100" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="overline mb-2 block text-slate-500">Your name (optional)</label>
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Chen"
                className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:border-[#2563EB] focus:outline-none"
              />
            </div>
            <div>
              <label className="overline mb-2 block text-slate-500">Email address</label>
              <div className="flex items-center border border-slate-200 bg-[#FAFAF7] pl-3">
                <Mail className="h-4 w-4 text-slate-400" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 border-none bg-transparent px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none"
                />
              </div>
            </div>
            <button
              data-testid="auth-email-submit"
              disabled={busy || !email.trim()}
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Continue with email
            </button>
          </form>

          <p className="overline text-center leading-relaxed text-slate-400">
            No password. No credit card. Your email is used only to save your projects and drafts.
          </p>
          <p className="text-center text-xs text-slate-500">
            Questions? Email us at{" "}
            <a href="mailto:hello@qualmitta.com" data-testid="auth-contact-email" className="text-[#93C5FD] hover:text-slate-900">
              hello@qualmitta.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M23 12.24c0-.79-.07-1.55-.2-2.29H12v4.34h6.16c-.27 1.42-1.08 2.62-2.3 3.42v2.84h3.72C21.72 18.5 23 15.62 23 12.24z"/>
      <path fill="#34A853" d="M12 23c3.1 0 5.7-1.03 7.6-2.79l-3.72-2.84c-1.03.69-2.35 1.1-3.88 1.1-2.98 0-5.5-2.01-6.4-4.71H1.75v2.94A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.6 13.76a6.6 6.6 0 0 1 0-3.52V7.3H1.75a11 11 0 0 0 0 9.4L5.6 13.76z"/>
      <path fill="#EA4335" d="M12 5.5c1.68 0 3.19.58 4.38 1.72l3.29-3.29C17.7 2.03 15.1 1 12 1A11 11 0 0 0 1.75 7.3L5.6 10.24c.9-2.7 3.42-4.74 6.4-4.74z"/>
    </svg>
  );
}
